import { __, sprintf } from "@wordpress/i18n";
import { useLocation } from "react-router-dom";
import QuickSettings from "@DashboardApp/pages/Welcome/QuickSettings";
import Astra_Admin_Icons from "@Common/block-icons";
import ProModules from "@DashboardApp/pages/Welcome/ProModules";
import UsefulPlugins from "@DashboardApp/pages/Welcome/UsefulPlugins";
import AstraIntegration from "@DashboardApp/pages/Welcome/AstraIntegration";
import BulkExtensionController from "@DashboardApp/pages/Welcome/BulkExtensionController";
import VideoPopup from "./VideoPopup";
import { useState } from "react";
import { ProButton, PromoCard } from "@astra-components";
import { Star } from "@astra-components/icons";

const classNames = (...classes) => classes.filter(Boolean).join(" ");

const Welcome = () => {
	const query = new URLSearchParams(useLocation()?.search);
	const displayBannerVideo = astra_admin.show_banner_video ? true : false;

	const allowAutoPlay =
		"1" === query.get("astra-activation-redirect") ? 1 : 0;

	const onCustomizeClick = () => {
		window.open(astra_admin.customize_url, "_self");
	};

	const [ videoPopup, setVideoPopup ] = useState( false );

	const toggleVideoPopup = () => {
		setVideoPopup( ! videoPopup );
	};

	return (
		<main className="py-[2.43rem]">
			<div className="max-w-3xl mx-auto px-6 lg:max-w-7xl">
				<h1 className="sr-only"> Astra </h1>

				{/* Banner section */}
				{astra_admin.show_self_branding && (
					<div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-5 lg:gap-0 xl:gap-0 rounded-md bg-white overflow-hidden shadow-sm px-8 py-8">
						<div className="grid grid-cols-1 gap-4 lg:col-span-3 h-full md:mr-[5.25rem]">
							<section aria-labelledby="section-1-title h-full">
								<h2 className="sr-only" id="section-1-title">
									{ __( 'Welcome Banner', 'astra' ) }
								</h2>
								<div className="flex flex-col justify-center h-full">
									<div className="">
										<p className="pb-4 font-medium text-base text-slate-800">
											{__("Hello ", "astra") +
												astra_admin.current_user +
												","}
										</p>
										<div className="flex">
											<h2 className="text-slate-800 text-[2rem] leading-10 pb-3 font-semibold text-left">
												{ sprintf( __( "Welcome to %s", "astra" ), astra_admin.theme_name ) }
											</h2>
											{ astra_admin.pro_available ? (
												<span className="ml-2 h-full inline-flex leading-[1rem] font-medium flex-shrink-0 py-[0rem] px-1.5 text-[0.625rem] text-white bg-slate-800 border border-slate-800 rounded-[0.1875rem] -tablet:mt:10">
													{ __( 'PRO', 'astra' ) }
												</span> )
											:
												( <span className="ml-2 h-full inline-flex leading-[1rem] flex-shrink-0 py-[0rem] px-1.5 text-[0.625rem] text-astra bg-blue-50 border border-blue-50 rounded-[0.1875rem] font-medium -tablet:mt:10">
													{ __( 'FREE', 'astra' ) }
												</span> )
											}
										</div>

										<p className="text-base leading-[1.625rem] text-slate-600 pb-7">
											{ sprintf(
												__(
													"%s is fast, fully customizable & beautiful WordPress theme suitable for blog, personal portfolio, business website and WooCommerce storefront. It is very lightweight and offers unparalleled speed.",
													"astra"
												),
												astra_admin.theme_name
											) }
										</p>

										<span className="relative z-0 inline-flex flex-col sm:flex-row justify-start w-full">
											<button
												type="button"
												className="sm:inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-astra focus-visible:bg-astra-hover hover:bg-astra-hover focus:outline-none mr-4 mb-2 sm:mb-0"
												onClick={onCustomizeClick}
											>
												{__(
													"Start Customising",
													"astra"
												)}
											</button>
											{
												displayBannerVideo && <button
													className="inline-flex items-center text-base font-medium text-astra focus:text-astra focus-visible:text-astra-hover active:text-astra-hover hover:text-astra-hover"
													onClick={ toggleVideoPopup }
												>
													<span className="pt-0.5 pl-4 pr-3">
														{" "}
														{
															Astra_Admin_Icons[
																"play"
															]
														}{" "}
													</span>
													{__(
														`Watch the Quick Starter Guide`,
														"astra"
													)}
												</button>
											}
										</span>
									</div>
								</div>
							</section>
						</div>

						<div className="grid grid-cols-1 gap-4 lg:col-span-2 h-full">
							<div className="astra-video-container">
								{/* Added rel=0 query paramter at the end to disable YouTube recommendations */}
								{
									displayBannerVideo && <iframe
										className="astra-video rounded-md"
										src={`https://www.youtube-nocookie.com/embed/TBZd9oligCw?showinfo=0&autoplay=${allowAutoPlay}&mute=${allowAutoPlay}&rel=0`}
										allow="autoplay"
										title="YouTube video player"
										frameBorder="0"
										allowFullScreen
									></iframe>
								}
							</div>
						</div>
					</div>
				)}

				{/* Left Column */}
				<div className="grid grid-cols-1 gap-[32px] items-start lg:grid-cols-3 lg:gap-[32px] xl:gap-[32px] mt-[32px]">
					{/* Left column */}
					<div
						className={classNames(
							astra_admin.show_self_branding
								? "lg:col-span-2"
								: "lg:col-span-3",
							"grid grid-cols-1 gap-[32px]"
						)}
					>
						<section aria-labelledby="section-1-title h-full">
							<h2 className="sr-only" id="section-1-title">
								{ __('Quick Links', 'astra' ) }
							</h2>
							<div className="p-[2rem] rounded-md bg-white overflow-hidden shadow-sm flex flex-col justify-center h-full">
								<div className="relative w-full flex items-center justify-between">
									<span className="font-semibold text-xl leading-6 text-slate-800">
										{" "}
										{__("Quick Settings", "astra")}{" "}
									</span>
									<a
										className="lg:flex-shrink-0 text-sm font-medium text-astra focus:text-astra focus-visible:text-astra-hover active:text-astra-hover hover:text-astra-hover no-underline"
										href={astra_admin.customize_url}
										target="_self"
										rel="noreferrer"
									>
										{__("Go to Customizer", "astra")}
									</a>
								</div>

								<QuickSettings />
							</div>
						</section>

						<section aria-labelledby="section-1-title h-full">
							<h2 className="sr-only" id="section-1-title">
								{ sprintf( __( 'Do more with %s Modules', 'astra' ), astra_admin.plugin_name ) }
							</h2>
							<div className="p-[2rem] rounded-md bg-white overflow-hidden shadow-sm flex flex-col justify-center h-full">
								<div className="relative w-full flex flex-col sm:flex-row sm:items-center sm:justify-between">
									<span className="font-semibold text-xl leading-6 text-slate-800 mb-4 sm:mb-0">
										{astra_admin.pro_available
											? sprintf(
												__( '%s Modules', 'astra' ),
												astra_admin.plugin_name
											  )
											: sprintf(
												__( 'Do more with %s Modules', 'astra' ),
												astra_admin.plugin_name
											)
										}
									</span>
									{astra_admin.pro_available && (
										<BulkExtensionController />
									)}
									{!astra_admin.pro_available && (
										<ProButton
											className="lg:flex-shrink-0 text-sm font-medium text-astra focus:text-astra focus-visible:text-astra-hover active:text-astra-hover hover:text-astra-hover no-underline"
											isLink={true}
										/>
									)}
								</div>

								{wp.hooks.applyFilters(
									`astra_dashboard.pro_extensions`,
									<ProModules />
								)}
							</div>
						</section>

						{astra_admin.show_plugins &&
							astra_admin.show_self_branding && (
								<section aria-labelledby="section-1-title h-full">
									<h2
										className="sr-only"
										id="section-1-title"
									>
										{ __( 'Astra Integrations', 'astra' ) }
									</h2>
									<div className="p-[2rem] rounded-md bg-white overflow-hidden shadow-sm flex flex-col justify-center h-full">
										<div className="relative w-full lg:flex lg:items-center lg:justify-between">
											<span className="font-semibold text-xl leading-6 text-slate-800">
												{ sprintf(
													__( '%s Integrations', "astra" ),
													astra_admin.theme_name
												) }
											</span>
										</div>

										<AstraIntegration />
									</div>
								</section>
							)}

						<section aria-labelledby="section-1-title h-full">
							<h2 className="sr-only" id="section-1-title">
								{ __( 'Your License', 'astra' ) }
							</h2>
							<div className="ast-welcome-screen rounded-md bg-white overflow-hidden shadow-sm flex flex-col justify-center h-full">
								{wp.hooks.applyFilters(
									`astra_dashboard.welcome_screen_after_integrations`,
									<span />
								)}
							</div>
						</section>
					</div>

					{/* Right Column */}
					{astra_admin.show_self_branding && (
						<div className="grid grid-cols-1 gap-[32px]">
							{astra_admin.show_plugins && (
								<section aria-labelledby="section-2-title">
									<h2
										className="sr-only"
										id="section-2-title"
									>
										{ __( 'Extend Your Website', 'astra' ) }
									</h2>
									<div className="box-border rounded-md shadow-sm overflow-hidden">
										<div className="">
											<h3 className="text-slate-800 text-base font-semibold leading-5 tracking-[0.0125rem] bg-white pt-6 pl-6 pb-4">
												{ __( 'Extend Your Website', 'astra' ) }
											</h3>
											<UsefulPlugins />
										</div>
									</div>
								</section>
							)}

							<PromoCard
								id="vip-priority-support"
								className="border border-sky-500"
								title={__("VIP Priority Support", "astra")}
								description={__(
									"Faster and exclusive support service designed for VIP assistance and benefits.",
									"astra"
								)}
								linkHRef={ astra_admin.astraWebsite?.vipPrioritySupportUrl }
								linkText={__("Know More →", "astra")}
							/>

							<PromoCard
								id="join-community"
								title={__("Join the Community", "astra")}
								description={__(
									"Got a question about the plugin, want to share your awesome project or just say hi? Join our wonderful community!",
									"astra"
								)}
								linkHRef="https://www.facebook.com/groups/wpastra"
								linkText={__("Join Now →", "astra")}
							/>
							<PromoCard
								id="rate-us"
								title={__("Rate Us", "astra")}
								linkHRef="https://wordpress.org/support/theme/astra/reviews/?rate=5#new-post"
								linkText={__("Submit a Review →", "astra")}
							>
								<p className="text-slate-500 text-sm pb-2.5 pr-12 flex items-center">
									<span className="text-xl text-slate-800 flex mr-2">
										{[1, 2, 3, 4, 5].map((item) => (
											<Star key={item} fill="#334155" />
										))}
									</span>
									<span className="text-xs leading-4 align-text-bottom text-slate-400">
										{__("Based on 5k+ reviews", "astra")}
									</span>
								</p>
								<p className="text-slate-500 text-sm pb-5">
									{__(
										"We love to hear from you, we would appreciate every single review.",
										"astra"
									)}
								</p>
							</PromoCard>
						</div>
					)}
				</div>
			</div>
			{
				displayBannerVideo && <VideoPopup allowAutoPlay={ allowAutoPlay } videoPopup={ videoPopup } toggleVideoPopup={ toggleVideoPopup } />
			}
		</main>
	);
};

export default Welcome;
